import { forwardRef, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { User, Calendar, FileText, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Skeleton } from 'skullet/react'
import type { SearchResult, SearchResultType } from '@/hooks/use-global-search'

interface GroupedResults {
  contacts: SearchResult[]
  leads: SearchResult[]
  events: SearchResult[]
  invoices: SearchResult[]
}

interface SearchResultsProps {
  results: GroupedResults
  isLoading: boolean
  isOpen: boolean
  onSelect: () => void
  selectedIndex: number
  onSelectedIndexChange: (index: number) => void
  className?: string
}

const SAMPLE_RESULTS: GroupedResults = {
  contacts: [
    { id: 'sample-contact', type: 'contact', title: 'Jordan Ellis', subtitle: 'jordan@example.com', href: '/' },
  ],
  leads: [
    { id: 'sample-lead', type: 'lead', title: 'Ellis rehearsal dinner', subtitle: 'Jun 14, 2026', href: '/', metadata: 'New' },
  ],
  events: [
    { id: 'sample-event', type: 'event', title: 'Ellis wedding reception', subtitle: 'Jun 15, 2026', href: '/' },
  ],
  invoices: [],
}

const categoryConfig: Record<
  SearchResultType,
  { label: string; icon: React.ComponentType<{ className?: string }> }
> = {
  contact: { label: 'Contacts', icon: User },
  lead: { label: 'Leads', icon: Users },
  event: { label: 'Events', icon: Calendar },
  invoice: { label: 'Invoices', icon: FileText },
}

interface ResultItemProps {
  result: SearchResult
  isSelected: boolean
  onSelect: () => void
  onMouseEnter: () => void
}

function ResultItem({ result, isSelected, onSelect, onMouseEnter }: ResultItemProps) {
  const config = categoryConfig[result.type]
  const Icon = config.icon

  return (
    <button
      type="button"
      className={cn(
        'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
        isSelected
          ? 'bg-accent text-accent-foreground'
          : 'hover:bg-accent/50'
      )}
      onClick={onSelect}
      onMouseEnter={onMouseEnter}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-background">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="truncate font-medium">{result.title}</div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="truncate">{result.subtitle}</span>
          {result.metadata && (
            <>
              <span className="text-muted-foreground/50">·</span>
              <span className="truncate">{result.metadata}</span>
            </>
          )}
        </div>
      </div>
    </button>
  )
}

interface ResultCategoryProps {
  label: string
  results: SearchResult[]
  startIndex: number
  selectedIndex: number
  onSelect: (result: SearchResult) => void
  onSelectedIndexChange: (index: number) => void
}

function ResultCategory({
  label,
  results,
  startIndex,
  selectedIndex,
  onSelect,
  onSelectedIndexChange,
}: ResultCategoryProps) {
  if (results.length === 0) return null

  return (
    <div className="py-1">
      <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground">
        {label}
      </div>
      {results.map((result, index) => (
        <ResultItem
          key={result.id}
          result={result}
          isSelected={selectedIndex === startIndex + index}
          onSelect={() => onSelect(result)}
          onMouseEnter={() => onSelectedIndexChange(startIndex + index)}
        />
      ))}
    </div>
  )
}

function EmptyState({ query }: { query?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-6 text-center">
      <div className="text-sm text-muted-foreground">
        {query ? `No results for "${query}"` : 'Start typing to search'}
      </div>
      <div className="mt-1 text-xs text-muted-foreground/70">
        Search contacts, leads, events, and invoices
      </div>
    </div>
  )
}

interface ResultCategoriesProps {
  results: GroupedResults
  selectedIndex: number
  onSelect: (result: SearchResult) => void
  onSelectedIndexChange: (index: number) => void
}

function ResultCategories({ results, selectedIndex, onSelect, onSelectedIndexChange }: ResultCategoriesProps) {
  // Calculate start indices for each category
  const contactsStart = 0
  const leadsStart = results.contacts.length
  const eventsStart = leadsStart + results.leads.length
  const invoicesStart = eventsStart + results.events.length

  return (
    <div className="divide-y">
      <ResultCategory
        label="Contacts"
        results={results.contacts}
        startIndex={contactsStart}
        selectedIndex={selectedIndex}
        onSelect={onSelect}
        onSelectedIndexChange={onSelectedIndexChange}
      />
      <ResultCategory
        label="Leads"
        results={results.leads}
        startIndex={leadsStart}
        selectedIndex={selectedIndex}
        onSelect={onSelect}
        onSelectedIndexChange={onSelectedIndexChange}
      />
      <ResultCategory
        label="Events"
        results={results.events}
        startIndex={eventsStart}
        selectedIndex={selectedIndex}
        onSelect={onSelect}
        onSelectedIndexChange={onSelectedIndexChange}
      />
      <ResultCategory
        label="Invoices"
        results={results.invoices}
        startIndex={invoicesStart}
        selectedIndex={selectedIndex}
        onSelect={onSelect}
        onSelectedIndexChange={onSelectedIndexChange}
      />
    </div>
  )
}

export const SearchResults = forwardRef<HTMLDivElement, SearchResultsProps & { query?: string }>(
  function SearchResults(
    {
      results,
      isLoading,
      isOpen,
      onSelect,
      selectedIndex,
      onSelectedIndexChange,
      query,
      className,
    },
    ref
  ) {
    const navigate = useNavigate()

    const handleSelect = useCallback(
      (result: SearchResult) => {
        navigate({ to: result.href })
        onSelect()
      },
      [navigate, onSelect]
    )

    const totalResults =
      results.contacts.length +
      results.leads.length +
      results.events.length +
      results.invoices.length

    if (!isOpen) return null

    return (
      <div
        ref={ref}
        className={cn(
          'absolute left-0 right-0 top-full z-50 mt-1 max-h-[400px] overflow-y-auto rounded-lg border bg-popover shadow-lg',
          className
        )}
      >
        <Skeleton
          data={isLoading ? null : results}
          sample={SAMPLE_RESULTS}
          label="Searching"
          empty={!isLoading && totalResults === 0}
          emptyMessage={<EmptyState query={query} />}
        >
          {(results) => (
            <ResultCategories
              results={results}
              selectedIndex={selectedIndex}
              onSelect={handleSelect}
              onSelectedIndexChange={onSelectedIndexChange}
            />
          )}
        </Skeleton>

        {/* Footer with keyboard hints */}
        {totalResults > 0 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                ↑↓
              </kbd>
              <span>to navigate</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                ↵
              </kbd>
              <span>to select</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                esc
              </kbd>
              <span>to close</span>
            </div>
          </div>
        )}
      </div>
    )
  }
)
